import React from 'react';
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CopyIcon,
  HandCoinsIcon,
  DownloadIcon,
} from '@hero/web-icons';

/**
 * Preset action types with built-in icon and color defaults.
 *
 * - `view`      — eye icon, neutral gray
 * - `edit`      — pencil icon, blue tint
 * - `delete`    — trash icon, red tint
 * - `duplicate` — copy icon, neutral gray
 * - `pay`       — hand-coins icon, primary (brand color)
 * - `download`  — download icon, neutral gray
 * - `custom`    — no defaults, requires explicit icon + className
 */
export type CellActionType =
  | 'view'
  | 'edit'
  | 'delete'
  | 'duplicate'
  | 'pay'
  | 'download'
  | 'custom';

export interface CellActionProps {
  /** Preset action type — auto-sets icon, color, and style */
  type?: CellActionType;
  /** Custom icon (overrides the preset icon) */
  icon?: React.ReactNode;
  /** Optional label displayed next to the icon (e.g. "Pay Now") */
  label?: string;
  /** Click handler */
  onClick?: () => void;
  /** Disabled state — shows the button grayed out */
  disabled?: boolean;
  /** Hidden — don't render the button at all */
  hidden?: boolean;
  /** Optional className override for the button */
  className?: string;
}

interface ActionPreset {
  icon: React.ReactNode;
  enabledClass: string;
  disabledClass: string;
  iconEnabledClass: string;
  iconDisabledClass: string;
}

const ICON_CLASS = 'w-4 h-4';
const DISABLED_ICON_CLASS = `${ICON_CLASS} text-gray-400`;

const actionPresets: Record<Exclude<CellActionType, 'custom'>, ActionPreset> = {
  view: {
    icon: <EyeIcon className={ICON_CLASS} />,
    enabledClass: 'bg-gray-100 hover:bg-gray-200 cursor-pointer',
    disabledClass: 'bg-gray-100 cursor-not-allowed',
    iconEnabledClass: `${ICON_CLASS} text-gray-600`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
  edit: {
    icon: <PencilIcon className={ICON_CLASS} />,
    enabledClass: 'bg-blue-50 hover:bg-blue-100 cursor-pointer',
    disabledClass: 'bg-gray-100 cursor-not-allowed',
    iconEnabledClass: `${ICON_CLASS} text-blue-600`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
  delete: {
    icon: <TrashIcon className={ICON_CLASS} />,
    enabledClass: 'bg-red-50 hover:bg-red-100 cursor-pointer',
    disabledClass: 'bg-gray-100 cursor-not-allowed',
    iconEnabledClass: `${ICON_CLASS} text-red-600`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
  duplicate: {
    icon: <CopyIcon className={ICON_CLASS} />,
    enabledClass: 'bg-gray-100 hover:bg-gray-200 cursor-pointer',
    disabledClass: 'bg-gray-100 cursor-not-allowed',
    iconEnabledClass: `${ICON_CLASS} text-gray-600`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
  pay: {
    icon: <HandCoinsIcon className={ICON_CLASS} />,
    enabledClass: 'bg-primary hover:bg-primary/90 cursor-pointer text-white',
    disabledClass: 'bg-gray-200 cursor-not-allowed text-gray-400',
    iconEnabledClass: `${ICON_CLASS} text-white`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
  download: {
    icon: <DownloadIcon className={ICON_CLASS} />,
    enabledClass: 'bg-gray-100 hover:bg-gray-200 cursor-pointer',
    disabledClass: 'bg-gray-100 cursor-not-allowed',
    iconEnabledClass: `${ICON_CLASS} text-gray-600`,
    iconDisabledClass: DISABLED_ICON_CLASS,
  },
};

/**
 * CellAction — Standardized action button for table cells.
 *
 * Use preset types for common actions (view, edit, delete, pay, etc.)
 * or `type="custom"` with explicit `icon` and `className` for one-offs.
 *
 * @example
 * ```tsx
 * // Icon-only preset
 * <CellAction type="view" onClick={openDrawer} />
 *
 * // Labeled button (icon + text)
 * <CellAction type="pay" label="Pay Now" onClick={handlePay} disabled={!canPay} hidden={!isPayer} />
 *
 * // Multiple actions
 * <div className="flex items-center gap-2">
 *   <CellAction type="view" onClick={openDrawer} />
 *   <CellAction type="delete" onClick={handleDelete} />
 * </div>
 *
 * // Custom override
 * <CellAction type="custom" icon={<MyIcon />} className="bg-purple-50" onClick={handleCustom} />
 * ```
 */
export const CellAction: React.FC<CellActionProps> = ({
  type = 'custom',
  icon,
  label,
  onClick,
  disabled = false,
  hidden = false,
  className,
}) => {
  if (hidden) return null;

  const preset = type !== 'custom' ? actionPresets[type] : null;

  // Resolve icon — custom icon prop takes priority, then preset, then nothing
  const resolvedIcon = icon ?? preset?.icon ?? null;
  if (!resolvedIcon && !label) return null;

  // Resolve button class
  const buttonClass =
    className ??
    (disabled
      ? preset?.disabledClass ?? 'bg-gray-100 cursor-not-allowed'
      : preset?.enabledClass ?? 'bg-gray-100 hover:bg-gray-200 cursor-pointer');

  // Clone icon element to apply the correct enabled/disabled class (only for preset icons without custom icon)
  const renderIcon = () => {
    if (icon) return icon; // custom icon — render as-is
    if (!preset) return resolvedIcon;

    const iconClass = disabled ? preset.iconDisabledClass : preset.iconEnabledClass;
    return React.cloneElement(preset.icon as React.ReactElement<{ className?: string }>, {
      className: iconClass,
    });
  };

  const baseClass = label
    ? `flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap w-fit transition-colors ${buttonClass}`
    : `p-2 rounded-full w-fit transition-colors ${buttonClass}`;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={baseClass}>
      {resolvedIcon && renderIcon()}
      {label}
    </button>
  );
};
